import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { BidHistoryFilters as Filters, BidOutcome } from '@/types/bidHistory';
import { JOB_CATEGORIES } from '@/lib/types/job';
import { Search, CalendarDays, X } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { DateRange } from 'react-day-picker';

interface BidHistoryFiltersProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
}

const OUTCOME_OPTIONS: { value: BidOutcome | 'all'; label: string }[] = [
  { value: 'all', label: 'All Outcomes' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'pending', label: 'Pending' },
  { value: 'expired', label: 'Expired' },
];

const DATE_PRESETS = [
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Last 6 months', days: 180 },
  { label: 'Last year', days: 365 },
  { label: 'All time', days: 3650 },
];

export function BidHistoryFilters({ filters, onFiltersChange }: BidHistoryFiltersProps) {
  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (range?.from) {
      onFiltersChange({
        ...filters,
        dateRange: {
          start: range.from,
          end: range.to || range.from,
        },
      });
    }
  };

  const handlePresetClick = (days: number) => {
    onFiltersChange({
      ...filters,
      dateRange: {
        start: subDays(new Date(), days),
        end: new Date(),
      },
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      dateRange: {
        start: subDays(new Date(), 90),
        end: new Date(),
      },
      outcome: 'all',
      category: 'all',
      search: '',
    });
  };

  const hasActiveFilters = 
    filters.outcome !== 'all' || 
    filters.category !== 'all' || 
    filters.search !== '';

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by job title, client, or RFQ ID..."
            value={filters.search}
            onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
            className="pl-9"
          />
        </div>

        {/* Date Range */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full sm:w-auto justify-start">
              <CalendarDays className="mr-2 h-4 w-4" />
              {format(filters.dateRange.start, 'MMM d')} - {format(filters.dateRange.end, 'MMM d, yyyy')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <div className="flex">
              <div className="border-r p-2 space-y-1">
                {DATE_PRESETS.map((preset) => (
                  <Button
                    key={preset.days}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-sm"
                    onClick={() => handlePresetClick(preset.days)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              <Calendar
                mode="range"
                selected={{
                  from: filters.dateRange.start,
                  to: filters.dateRange.end,
                }}
                onSelect={handleDateRangeChange}
                numberOfMonths={2}
                disabled={{ after: new Date() }}
              />
            </div>
          </PopoverContent>
        </Popover>

        {/* Outcome Filter */}
        <Select
          value={filters.outcome}
          onValueChange={(value) => onFiltersChange({ ...filters, outcome: value as BidOutcome | 'all' })}
        >
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Outcome" />
          </SelectTrigger>
          <SelectContent>
            {OUTCOME_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Category Filter */}
        <Select
          value={filters.category}
          onValueChange={(value) => onFiltersChange({ ...filters, category: value })}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {JOB_CATEGORIES.map((category) => (
              <SelectItem key={category.value} value={category.value}>
                {category.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClearFilters}
            className="shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
